import axios from 'axios';
import React, { useRef, useState } from 'react'
import { toast, ToastContainer } from 'react-toastify';
import * as XLSX from 'xlsx';
import { BASE_URL } from '../../config/axios';
import { handleErrorToast, ProgressModal } from '../../components/CustomFunctions';
import { AlertCircle, ClipboardList, Download, FileText, FileTextIcon, FlaskConical, Upload, UploadCloud, X } from 'lucide-react';
import { Modal } from 'react-bootstrap';
const UploadProjectDetails = ({ getProjects }) => {

    const [show, setShow] = useState(false);
    const [fileName, setFileName] = useState("");
    const [fileSize, setFileSize] = useState("");
    const [excelJson, setExcelJson] = useState(null);
    const [docErrors, setDocErrors] = useState({});
    const [isDragging, setIsDragging] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    const fileInputRef = useRef(null);

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const handleDownload = () => {
        const headers = [
            "project_code", "project_name", "project_status", "client_detail", "project_scope",
            "test_performed", "lab_sample_quantity", "department_equipment", "responsible_person", "testing_person",
            "project_confirmation_date", "mobilization_date", "survey_date", "completion_date",
            "sample_receiving_date_ho", "project_data_received_from_client",
            "draft_report_submission_date", "final_report_submission_date",
            "total_duration", "remark_1", "remark_2"
        ];

        const data = [
            headers,
            [],
            ["GENERAL INSTRUCTIONS:"],
            ["   • Project code should be unique."],
            ["   • All Dates should be in DD-MM-YYYY format."],
            [],
            ["PROJECT STATUS OPTIONS:"],
            ["   • Open"],
            ["   • Ongoing"],
            ["   • Closed"],
            [],
            ["NOTE: Project Confirmation Date and Mobilization Date are mandatory."]
        ];

        const ws = XLSX.utils.aoa_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Project Format");

        const wbout = XLSX.write(wb, { bookType: "xlsx", type: "binary" });

        const blob = new Blob([s2ab(wbout)], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "Project_Upload_Format.xlsx";
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const s2ab = (s) => {
        const buf = new ArrayBuffer(s.length);
        const view = new Uint8Array(buf);
        for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xff;
        return buf;
    };

    const handleClose = () => {
        setFileName("");
        setFileSize("");
        setExcelJson(null);
        setDocErrors({});
        setShow(false);
    };

    const handleShow = () => setShow(true);

    const handleRemoveFile = () => {
        setFileName("");
        setFileSize("");
        setExcelJson(null);
        setDocErrors({});
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const processFile = (file) => {
        if (!file) return;
        const validFileTypes = ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "application/vnd.ms-excel"];
        if (!validFileTypes.includes(file.type)) {
            setDocErrors({ document_file: "Please upload a valid Excel file (.xlsx or .xls)." });
            setFileName("");
            setFileSize("");
            return;
        }

        setFileName(file.name);
        setFileSize(formatFileSize(file.size));
        setDocErrors({});
        readExcel(file);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        processFile(file);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        processFile(file);
    };

    const readExcel = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: "array" });

            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

            const headerIndex = jsonData.findIndex(row => row.includes("project_code"));

            if (headerIndex === -1) {
                toast.error("Header row not found in Excel");
                return;
            }

            const slicedData = jsonData.slice(headerIndex);

            const filteredRows = slicedData.filter(row => {
                const firstCell = row[0]?.toString().trim() || "";
                return (
                    row.length > 0 &&
                    !firstCell.includes("GENERAL INSTRUCTIONS") &&
                    !firstCell.includes("PROJECT STATUS OPTIONS") &&
                    !firstCell.includes("NOTE") &&
                    !/^[•\-*]/.test(firstCell)
                );
            });

            const [headers, ...dataRows] = filteredRows;
            const finalData = dataRows.map(row =>
                Object.fromEntries(headers.map((key, i) => [key, row[i] ?? ""]))
            );

            const dateFields = [
                "project_confirmation_date", "mobilization_date", "survey_date", "completion_date",
                "sample_receiving_date_ho", "project_data_received_from_client",
                "draft_report_submission_date", "final_report_submission_date"
            ];

            const convertToYYYYMMDD = (input) => {
                try {
                    let date;
                    if (typeof input === "number") {
                        const excelEpoch = new Date(1900, 0, 1);
                        const daysOffset = input - 1;
                        date = new Date(excelEpoch.getTime() + daysOffset * 86400000);
                    } else if (/^\d{4}\.\d{2}\.\d{2}$/.test(input)) {
                        const [year, month, day] = input.split('.');
                        date = new Date(`${year}-${month}-${day}`);
                    } else if (/^\d{2}\.\d{2}\.\d{4}$/.test(input)) {
                        const [day, month, year] = input.split('.');
                        date = new Date(`${year}-${month}-${day}`);
                    } else if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
                        date = new Date(input);
                    } else if (/^\d{2}-\d{2}-\d{4}$/.test(input)) {
                        const [day, month, year] = input.split('-');
                        date = new Date(`${year}-${month}-${day}`);
                    } else {
                        date = new Date(input);
                    }

                    if (isNaN(date.getTime())) {
                        throw new Error('Invalid date format');
                    }

                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                } catch {
                    return null;
                }
            };

            const zeroFields = ["total_duration", "lab_sample_quantity"];

            const formattedJson = finalData.map((row) => {
                const obj = {};
                headers.forEach((header) => {
                    const rawValue = row[header];
                    const isEmpty = rawValue === undefined || rawValue === null || rawValue === "";

                    if (dateFields.includes(header)) {
                        obj[header] = isEmpty ? null : convertToYYYYMMDD(rawValue);
                    } else if (zeroFields.includes(header)) {
                        obj[header] = isEmpty ? 0 : rawValue;
                    } else {
                        obj[header] = isEmpty ? null : rawValue;
                    }
                });
                return obj;
            });

            formattedJson.forEach((item, index) => {
                if (typeof item !== 'object' || item === null) {
                    toast.error(`Row ${index + 2} in Excel is invalid.`);
                    return;
                }
                if (!item.project_code) {
                    toast.error(`Project code missing in row ${index + 2}.`);
                }
            });

            setExcelJson(formattedJson);
        };

        reader.readAsArrayBuffer(file);
    };

    const handleUpload = async () => {
        if (excelJson) {
            setShowProgressModal(true);
            setUploadProgress(0);

            const loadingToastId = toast.loading("Uploading: Please wait...");

            let progress = 0;
            const interval = setInterval(() => {
                progress += 10;
                if (progress >= 100) {
                    progress = 100;
                    clearInterval(interval);
                }
                setUploadProgress(progress);
            }, 100);

            try {
                // NOTE: confirm this bulk endpoint with backend — following the `<resource>bulk/` convention used for employeebulk
                const res = await axios.post(`${BASE_URL}/labmonitoring/labjobbulk/`, excelJson);
                if (res.status === 200 || res.status === 201) {
                    await getProjects?.();
                    toast.dismiss(loadingToastId);
                    toast.success("Uploaded Successfully");
                    handleClose();
                }
            } catch (err) {
                clearInterval(interval);
                setUploadProgress(0);
                toast.dismiss(loadingToastId);
                handleErrorToast(err);
            } finally {
                clearInterval(interval);
                setTimeout(() => {
                    setShowProgressModal(false);
                }, 1500);
                toast.dismiss(loadingToastId);
            }
        } else {
            setDocErrors({ document_file: "Please upload a valid Excel file." });
        }
    };

    return (
        <>
            <style>{`
                .pjb-upload-zone {
                    border: 2px dashed #c7cdd6;
                    border-radius: 14px;
                    background: #fafbfd;
                    padding: 32px 20px;
                    text-align: center;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    position: relative;
                }
                .pjb-upload-zone:hover {
                    border-color: #6366f1;
                    background: #f5f5ff;
                }
                .pjb-upload-zone.dragging {
                    border-color: #6366f1;
                    background: #eef0ff;
                    transform: scale(1.01);
                }
                .pjb-upload-zone.has-error {
                    border-color: #e11d48;
                    background: #fff1f2;
                }
                .pjb-upload-icon-circle {
                    width: 56px;
                    height: 56px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #6366f1, #8b5cf6);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 14px;
                    color: white;
                    box-shadow: 0 4px 14px rgba(99, 102, 241, 0.3);
                }
                .pjb-upload-title {
                    font-size: 15px;
                    font-weight: 600;
                    color: #1f2937;
                    margin: 0 0 4px;
                }
                .pjb-upload-subtitle {
                    font-size: 13px;
                    color: #6b7280;
                    margin: 0;
                }
                .pjb-upload-browse {
                    color: #6366f1;
                    font-weight: 600;
                    text-decoration: underline;
                }
                .pjb-file-card {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    background: #f0fdf4;
                    border: 1px solid #bbf7d0;
                    border-radius: 12px;
                    padding: 14px 16px;
                    margin-top: 12px;
                    animation: pjb-fade-in 0.25s ease;
                }
                .pjb-file-icon {
                    width: 40px;
                    height: 40px;
                    border-radius: 10px;
                    background: #16a34a;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    flex-shrink: 0;
                }
                .pjb-file-info {
                    flex: 1;
                    min-width: 0;
                }
                .pjb-file-name {
                    font-size: 14px;
                    font-weight: 600;
                    color: #1f2937;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .pjb-file-size {
                    font-size: 12px;
                    color: #6b7280;
                }
                .pjb-file-remove {
                    background: none;
                    border: none;
                    cursor: pointer;
                    color: #9ca3af;
                    padding: 6px;
                    border-radius: 8px;
                    display: flex;
                    transition: all 0.15s ease;
                    flex-shrink: 0;
                }
                .pjb-file-remove:hover {
                    background: #fee2e2;
                    color: #dc2626;
                }
                @keyframes pjb-fade-in {
                    from { opacity: 0; transform: translateY(-4px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .pjb-preview-wrap {
                    border: 1px solid #e5e7eb;
                    border-radius: 14px;
                    overflow: hidden;
                    margin-top: 8px;
                }
                .pjb-preview-scroll {
                    overflow-x: auto;
                    max-height: 340px;
                    overflow-y: auto;
                }
                .pjb-preview-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 13px;
                    white-space: nowrap;
                }
                .pjb-preview-table thead th {
                    position: sticky;
                    top: 0;
                    background: #f8fafc;
                    color: #475569;
                    font-weight: 600;
                    text-align: left;
                    padding: 10px 14px;
                    border-bottom: 2px solid #e2e8f0;
                    z-index: 1;
                }
                .pjb-preview-table tbody td {
                    padding: 9px 14px;
                    color: #334155;
                    border-bottom: 1px solid #f1f5f9;
                }
                .pjb-preview-table tbody tr:nth-child(even) {
                    background: #fbfcfe;
                }
                .pjb-preview-table tbody tr:hover {
                    background: #f0f4ff;
                }
                .pjb-preview-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 10px 4px;
                }
                .pjb-preview-badge {
                    background: #eef2ff;
                    color: #4338ca;
                    font-size: 12px;
                    font-weight: 600;
                    padding: 4px 10px;
                    border-radius: 999px;
                }
                .pjb-download-row {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    background: #f8fafc;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 14px 16px;
                    margin-bottom: 18px;
                    gap: 12px;
                }
                .pjb-download-row p {
                    margin: 0;
                    font-size: 13.5px;
                    color: #475569;
                }
            `}</style>

            <button title="Bulk Upload Projects" className="btn-animated" onClick={handleShow} style={{ margin: "2px" }}>
                <UploadCloud size={16} style={{ marginRight: "6px" }} /> Bulk Upload
            </button>

            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Upload" />
            ) : (
                <Modal show={show} onHide={handleClose} dialogClassName="modern-project-modal model-width80vw" size="xl">
                    <Modal.Header className="modern-modal-header">
                        <div className="header-content">
                            <div style={{ color: "white" }}>
                                <FlaskConical size={24} />
                            </div>
                            <div>
                                <Modal.Title>Bulk Upload Projects</Modal.Title>
                                <p className="header-subtitle" style={{ color: "white" }}>Upload multiple lab projects at once using the Excel format</p>
                            </div>
                        </div>
                        <button className="close-btn" onClick={handleClose}>
                            <X size={20} />
                        </button>
                    </Modal.Header>

                    <Modal.Body className="modern-modal-body">

                        <div className="pjb-download-row">
                            <p><FileTextIcon size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                Download the Excel template, fill in project details, then upload it below.
                            </p>
                            <button onClick={handleDownload} className="btn-primaryy" type="button" style={{ flexShrink: 0 }}>
                                <Download size={16} />
                                <span>Format</span>
                            </button>
                        </div>

                        <div className="section-title">
                            <Upload size={18} />
                            <h4>Upload File</h4>
                        </div>

                        <div
                            className={`pjb-upload-zone ${isDragging ? 'dragging' : ''} ${docErrors.document_file ? 'has-error' : ''}`}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <div className="pjb-upload-icon-circle">
                                <Upload size={24} />
                            </div>
                            <p className="pjb-upload-title">
                                Drag & drop your Excel file here
                            </p>
                            <p className="pjb-upload-subtitle">
                                or <span className="pjb-upload-browse">browse</span> to choose a file &nbsp;·&nbsp; .xlsx or .xls
                            </p>
                            <input
                                ref={fileInputRef}
                                id="document_file"
                                name="document_file"
                                type="file"
                                accept=".xlsx, .xls"
                                onChange={handleFileChange}
                                style={{ display: "none" }}
                            />
                        </div>

                        {docErrors.document_file && (
                            <div className="tab-error-message" style={{ marginTop: '10px' }}>
                                <AlertCircle size={14} />
                                <span>{docErrors.document_file}</span>
                            </div>
                        )}

                        {fileName && !docErrors.document_file && (
                            <div className="pjb-file-card">
                                <div className="pjb-file-icon">
                                    <FileText size={20} />
                                </div>
                                <div className="pjb-file-info">
                                    <div className="pjb-file-name">{fileName}</div>
                                    <div className="pjb-file-size">{fileSize}{excelJson ? ` · ${excelJson.length} records found` : ''}</div>
                                </div>
                                <button type="button" className="pjb-file-remove" onClick={handleRemoveFile} title="Remove file">
                                    <X size={18} />
                                </button>
                            </div>
                        )}

                        {excelJson && excelJson.length > 0 && (
                            <>
                                <div className="pjb-preview-header">
                                    <div className="section-title" style={{ marginBottom: 0 }}>
                                        <ClipboardList size={18} />
                                        <h4 style={{ margin: 0 }}>Preview</h4>
                                    </div>
                                    <span className="pjb-preview-badge">{excelJson.length} projects</span>
                                </div>

                                <div className="pjb-preview-wrap">
                                    <div className="pjb-preview-scroll table-box scroll-container-table">
                                        <table className="pjb-preview-table">
                                            <thead className="table-heading">
                                                <tr>
                                                    <th>Project Code</th>
                                                    <th>Project Name</th>
                                                    <th>Status</th>
                                                    <th>Client</th>
                                                    <th>Scope</th>
                                                    <th>Test Performed</th>
                                                    <th>Lab Qty</th>
                                                    <th>Dept/Equipment</th>
                                                    <th>Responsible</th>
                                                    <th>Testing Person(s)</th>
                                                    <th>Confirmation Date</th>
                                                    <th>Mobilization Date</th>
                                                    <th>Survey Date</th>
                                                    <th>Completion Date</th>
                                                    <th>Sample Receiving</th>
                                                    <th>Data Received</th>
                                                    <th>Draft Submission</th>
                                                    <th>Final Submission</th>
                                                    <th>Duration</th>
                                                    <th>Remark 1</th>
                                                    <th>Remark 2</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {excelJson.map((project, index) => (
                                                    <tr key={index}>
                                                        <td>{project.project_code}</td>
                                                        <td>{project.project_name}</td>
                                                        <td>{project.project_status}</td>
                                                        <td>{project.client_detail}</td>
                                                        <td>{project.project_scope}</td>
                                                        <td>{project.test_performed}</td>
                                                        <td>{project.lab_sample_quantity}</td>
                                                        <td>{project.department_equipment}</td>
                                                        <td>{project.responsible_person}</td>
                                                        <td>{project.testing_person}</td>
                                                        <td>{project.project_confirmation_date}</td>
                                                        <td>{project.mobilization_date}</td>
                                                        <td>{project.survey_date}</td>
                                                        <td>{project.completion_date}</td>
                                                        <td>{project.sample_receiving_date_ho}</td>
                                                        <td>{project.project_data_received_from_client}</td>
                                                        <td>{project.draft_report_submission_date}</td>
                                                        <td>{project.final_report_submission_date}</td>
                                                        <td>{project.total_duration}</td>
                                                        <td>{project.remark_1}</td>
                                                        <td>{project.remark_2}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </>
                        )}

                        <div className="form-actions">
                            <button type="button" className="btn-secondaryy" onClick={handleClose}>
                                Cancel
                            </button>
                            <button type="button" className="btn-primaryy" onClick={handleUpload} disabled={!excelJson}>
                                <Upload size={18} />
                                <span>Upload</span>
                            </button>
                        </div>

                        <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};

export { UploadProjectDetails };